import { useTheme } from '@mui/material/styles';

import type { ColorTokens } from '../tokens';

/**
 * Couleurs et motifs des series de tous les graphiques.
 *
 * **Une seule liste, dans un ordre fixe** : la premiere serie est toujours
 * `primary`, la deuxieme `infoText`, etc. Cinq entrees, pas six : les maquettes
 * n'en emploient jamais plus, et `accentPinkStrong` ne se distinguait pas de
 * `primary` (environ 1,8 en ecart de couleur CIE76 en clair). Le rang est aussi ce que la legende
 * et la table masquee racontent, si bien que la couleur ne porte jamais seule
 * la distinction (WCAG 1.4.1).
 *
 * **Ce ne sont pas les aplats de la maquette.** Elle peint ses series avec
 * `info` et `success`, qui sont des aplats de charte (`#8EC5FF`, `#A8D5C2`) :
 * environ 1,7:1 sur une carte blanche, bien sous le 3:1 qu'exige WCAG 1.4.11
 * d'un objet graphique. Les encres `infoText` et `successText` en sont la
 * version qui tient le seuil dans les **deux** themes ; l'ecart est assume et
 * verifie par `tokens.contrast.test.ts`, qui lit cette liste.
 */
export const SERIES_TOKENS = [
  'primary',
  'infoText',
  'successText',
  'accentYellowStrong',
  'textMuted',
] as const satisfies readonly (keyof ColorTokens)[];

export type SeriesToken = (typeof SERIES_TOKENS)[number];

/**
 * Motifs de trait des courbes, dans le meme ordre : un second repere, avec la
 * legende, pour distinguer deux courbes sans la couleur. `undefined` = plein.
 */
export const LINE_DASHES: readonly (string | undefined)[] = [undefined, '7 4', '2 3', '9 3 2 3', '4 4'];

/** Couleur de la serie de rang `index` ; au-dela de cinq, la liste recommence. */
export const seriesTokenAt = (index: number): SeriesToken => SERIES_TOKENS[index % SERIES_TOKENS.length]!;

export const lineDashAt = (index: number): string | undefined => LINE_DASHES[index % LINE_DASHES.length];

/** Jetons du mode courant : un graphique SVG ne peut pas lire `sx="tokens.x"`. */
export const useTokens = (): ColorTokens => useTheme().palette.tokens;
